import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { C } from '../albumColors';
import { stickerCode } from '../albumData';

/**
 * Sticky banner that replaces the TradeDetailModal overlay when navigating
 * from /album/dashboard to /album?tradeId=... or ?offerId=...
 *
 * Shows at the top of the album content area (inside <main>, above the grid).
 * The album grid remains fully interactive underneath.
 */
function StickerChip({ n, accent }) {
  return (
    <span
      className="px-1.5 py-0.5 rounded text-[10px] font-bold"
      style={{ backgroundColor: `${accent}22`, color: accent }}
    >
      {stickerCode(n)}
    </span>
  );
}

function ChipGroup({ nums, accent, label, maxVisible = 5 }) {
  if (!nums || nums.length === 0) return null;
  const visible = nums.slice(0, maxVisible);
  const extra = nums.length - maxVisible;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {label && (
        <span
          className="text-[9px] font-black uppercase tracking-widest mr-1 shrink-0"
          style={{ color: accent }}
        >
          {label}
        </span>
      )}
      {visible.map(n => <StickerChip key={n} n={n} accent={accent} />)}
      {extra > 0 && (
        <span className="text-[10px]" style={{ color: C.onSurfaceVariant }}>+{extra}</span>
      )}
    </div>
  );
}

function TradeContextBanner({
  trade,
  offer,
  myUserId,
  myName,
  onClose,
  onAccept,
  onReject,
  onCancel,
  onComplete,
  onWhatsapp,
  onClaim,
  onCancelOffer,
  theme,
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const surfaceBg     = theme?.actionBarBg     ?? C.surface;
  const surfaceBorder = theme?.actionBarBorder  ?? `${C.outlineVariant}33`;
  const headerText    = theme?.actionBarText    ?? C.onSurface;
  const mutedText     = theme?.mutedText        ?? C.onSurfaceVariant;

  async function wrap(fn) {
    if (!fn || busy) return;
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  }

  /* ─── Trade banner ─────────────────────────────────────────── */
  if (trade) {
    const iAmProposer = trade.direction === 'outgoing'
      || (trade.direction == null && trade.proposerId === myUserId);
    const isIncoming  = trade.direction === 'incoming'
      || (trade.direction == null && trade.receiverId === myUserId);
    const status = trade.status || 'PENDING';

    // Status chip colours
    const chipStyle = status === 'ACCEPTED'
      ? { bg: '#2563eb22', text: '#2563eb' }
      : status === 'COMPLETED'
      ? { bg: `${C.onSurface}11`, text: C.onSurfaceVariant }
      : (status === 'CANCELLED' || status === 'REJECTED')
      ? { bg: '#ef444422', text: '#b91c1c' }
      : /* PENDING */ { bg: '#f59e0b22', text: '#b45309' };
    const statusLabel = {
      ACCEPTED: 'Reservado',
      CANCELLED: 'Cancelado',
      COMPLETED: 'Completado',
      PENDING: 'Pendiente',
      REJECTED: 'Rechazado',
    }[status] || status;

    return (
      <div
        className="mx-3 my-2 rounded-xl px-3 py-2.5 flex items-start gap-3 flex-wrap"
        style={{
          backgroundColor: surfaceBg,
          border: `1px solid ${surfaceBorder}`,
          boxShadow: '0 2px 12px -4px rgba(0,0,0,0.12)',
        }}
      >
        {/* Avatar */}
        {trade.otherUser && (
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 mt-0.5"
            style={{ backgroundColor: trade.otherUser.color || '#2563eb' }}
          >
            {trade.otherUser.initial || '?'}
          </div>
        )}

        {/* Name + status */}
        <div className="flex flex-col min-w-0 shrink-0" style={{ maxWidth: 180 }}>
          <span className="text-sm font-bold truncate" style={{ color: headerText }}>
            {trade.otherUser?.name || t('Usuario')}
          </span>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded-full"
              style={{ backgroundColor: chipStyle.bg, color: chipStyle.text }}
            >
              {t(statusLabel)}
            </span>
            {trade.createdAt && (
              <span className="text-[10px]" style={{ color: mutedText }}>
                {t('Trade #{{id}}', { id: trade.id })}
              </span>
            )}
          </div>
        </div>

        {/* Sticker chips */}
        <div className="flex flex-col gap-1 flex-1 min-w-[120px]">
          <ChipGroup nums={trade.youGive}    accent="#ef4444" label={t('Das')} />
          <ChipGroup nums={trade.youReceive} accent="#16a34a" label={t('Recibís')} />
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          {/* PENDING (incoming Aceptar/Rechazar, outgoing Cancelar propuesta)
              ahora viven exclusivamente en el AlbumActionBar TRADE_REVIEW.
              No los duplicamos acá. */}

          {/* ACCEPTED: WhatsApp + Cancel + Complete */}
          {(status === 'ACCEPTED' || status === 'COMPLETED') && onWhatsapp && (
            <button
              disabled={busy}
              onClick={() => wrap(onWhatsapp)}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold flex items-center gap-1 transition-all disabled:opacity-50"
              style={{ backgroundColor: '#25D36622', color: '#15803d', border: '1px solid #25D36644' }}
            >
              <span className="material-symbols-outlined text-sm" style={{ color: '#25D366' }}>chat</span>
              {t('WhatsApp')}
            </button>
          )}
          {status === 'ACCEPTED' && onCancel && (
            <button
              disabled={busy}
              onClick={() => wrap(onCancel)}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all disabled:opacity-50"
              style={{ color: '#ef4444', border: '1px solid #ef444455', backgroundColor: 'transparent' }}
            >
              {t('Cancelar reserva')}
            </button>
          )}
          {status === 'ACCEPTED' && onComplete && (
            <button
              disabled={busy}
              onClick={() => wrap(onComplete)}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white flex items-center gap-1 transition-all disabled:opacity-50"
              style={{ backgroundColor: '#2563eb' }}
            >
              <span className="material-symbols-outlined text-sm">check_circle</span>
              {t('Completar')}
            </button>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors"
            aria-label={t('Cerrar')}
          >
            <span className="material-symbols-outlined text-base" style={{ color: mutedText }}>close</span>
          </button>
        </div>
      </div>
    );
  }

  /* ─── Offer banner ─────────────────────────────────────────── */
  if (offer) {
    const isMine  = offer.userId === myUserId;
    const isOpen  = offer.status === 'OPEN';
    const chipStyle = isOpen
      ? { bg: '#2563eb22', text: '#2563eb' }
      : { bg: `${C.onSurface}11`, text: C.onSurfaceVariant };

    return (
      <div
        className="mx-3 my-2 rounded-xl px-3 py-2.5 flex items-start gap-3 flex-wrap"
        style={{
          backgroundColor: surfaceBg,
          border: `1px solid ${surfaceBorder}`,
          boxShadow: '0 2px 12px -4px rgba(0,0,0,0.12)',
        }}
      >
        {/* Icon */}
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-0.5"
          style={{ backgroundColor: '#2563eb22' }}
        >
          <span className="material-symbols-outlined text-base" style={{ color: '#2563eb' }}>public</span>
        </div>

        {/* Title + status */}
        <div className="flex flex-col min-w-0 shrink-0" style={{ maxWidth: 180 }}>
          <span className="text-sm font-bold truncate" style={{ color: headerText }}>
            {t('Oferta pública')}
          </span>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded-full"
              style={{ backgroundColor: chipStyle.bg, color: chipStyle.text }}
            >
              {t(isOpen ? 'Abierta' : 'Cerrada')}
            </span>
            <span className="text-[10px]" style={{ color: mutedText }}>
              {t('Oferta #{{id}}', { id: offer.id })}
            </span>
          </div>
        </div>

        {/* Sticker chips */}
        <div className="flex flex-col gap-1 flex-1 min-w-[120px]">
          <ChipGroup nums={offer.offered}   accent="#ef4444" label={t('Das')} />
          <ChipGroup nums={offer.requested} accent="#16a34a" label={t('Recibís')} />
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          {isOpen && !isMine && onClaim && (
            <button
              disabled={busy}
              onClick={() => wrap(onClaim)}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white transition-all disabled:opacity-50"
              style={{ backgroundColor: '#2563eb' }}
            >
              {t('Reclamar')}
            </button>
          )}
          {isOpen && isMine && onCancelOffer && (
            <button
              disabled={busy}
              onClick={() => wrap(onCancelOffer)}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all disabled:opacity-50"
              style={{ color: '#ef4444', border: '1px solid #ef444455', backgroundColor: 'transparent' }}
            >
              {t('Cancelar oferta')}
            </button>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors"
            aria-label={t('Cerrar')}
          >
            <span className="material-symbols-outlined text-base" style={{ color: mutedText }}>close</span>
          </button>
        </div>
      </div>
    );
  }

  return null;
}

export default TradeContextBanner;
